import type { InputHTMLAttributes } from 'react';

/**
 * A field for a credential that belongs to the *business*, not to the person typing it.
 *
 * ## Why this is its own component
 *
 * A browser cannot tell the difference between the two kinds of password field on this
 * console. One is the operator signing in, where offering to remember the password is exactly
 * right. The other is a Paystack secret key, a vendor API token, an SMTP password: values that
 * belong to the company, are typed once, and have nothing to do with whoever is at the
 * keyboard. A password manager filling one of those is not a convenience, it is a wrong value
 * silently pasted into a field that decides whether the platform can take payments.
 *
 * It has already happened here in the worse direction: the Paystack secret field held a
 * sixteen-character string that was never a Paystack key, every call to the vendor came back
 * `401`, and money landed in customers' dedicated accounts and never reached their wallets. A
 * field that autofills is a field where that happens without anybody typing anything.
 *
 * So the rule is: the sign-in form keeps every browser convenience, and every field on an
 * *operations* screen refuses all of them.
 *
 * ## Why it takes five attributes to say one thing
 *
 * There is no standard for "do not fill this". `autocomplete="off"` is the obvious answer and
 * Chrome deliberately ignores it on password fields, because sites used it to stop people
 * using password managers at all. What Chrome does honour is `new-password`, which means "this
 * is a password being *set*, so offer to generate one rather than filling a saved one".
 *
 * The third-party managers each read their own attribute and ignore everyone else's, so
 * 1Password, LastPass, Bitwarden and Dashlane are named individually. They are inert
 * everywhere else and cost nothing; a manager not on the list fills the field and somebody
 * has a very confusing afternoon.
 *
 * The `name` matters as much as the rest. Heuristic fillers match on it, so a field called
 * `password` or `api-key` is one they will volunteer for however it is marked. These are
 * named after the vendor field they hold, which is also what makes an error message about
 * them readable.
 */
export function SecretInput({
  fieldName,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  /**
   * A stable name for this field, used only to keep fillers away from it.
   *
   * Prefixed rather than passed through, so a field legitimately called "password" cannot
   * reach the DOM under that name and attract a filler by its heuristics alone.
   */
  readonly fieldName: string;
}): JSX.Element {
  return (
    <input
      {...rest}
      name={`genex-setting-${fieldName}`}
      // Chrome and Safari: "a password being set", which suppresses filling a saved one.
      // `off` is ignored on password fields by design, so it is not what is used here.
      autoComplete="new-password"
      // Each manager reads its own and ignores the others'. Inert everywhere else.
      data-1p-ignore="true"
      data-lpignore="true"
      data-bwignore="true"
      data-form-type="other"
      // Autocorrect and capitalisation mangle a key on a touch keyboard, and a mangled key
      // fails exactly like a wrong one: a 401 from the vendor and no clue on any screen.
      spellCheck={false}
      autoCapitalize="off"
      autoCorrect="off"
      className={`input${rest.className === undefined ? '' : ` ${rest.className}`}`}
    />
  );
}
